/**
 * Contrast measurement for gallery specimens: the WCAG 2.2 contrast ratio of a
 * rendered colour against the colour actually behind it, with translucent
 * layers (glass, scrims) composited over their ancestors.
 *
 * Relative luminance and the ratio follow WCAG 2.2, Success Criterion 1.4.3
 * ("contrast ratio", "relative luminance" in the glossary).
 */

export interface FmRgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

const RGB_RE = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/;
const SRGB_RE =
  /^color\(srgb\s+([\d.e-]+)\s+([\d.e-]+)\s+([\d.e-]+)(?:\s*\/\s*([\d.e-]+%?))?\s*\)$/;

function alpha(token: string | undefined): number {
  if (token === undefined) return 1;
  return token.endsWith('%') ? Number(token.slice(0, -1)) / 100 : Number(token);
}

/** Parses a computed colour (`rgb()`, `rgba()` or `color(srgb …)`); anything else is refused. */
export function parseComputedColor(value: string): FmRgba {
  const text = value.trim();
  if (text === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };
  const rgb = RGB_RE.exec(text);
  if (rgb) return { r: Number(rgb[1]), g: Number(rgb[2]), b: Number(rgb[3]), a: alpha(rgb[4]) };
  const srgb = SRGB_RE.exec(text);
  if (srgb) {
    return {
      r: Number(srgb[1]) * 255,
      g: Number(srgb[2]) * 255,
      b: Number(srgb[3]) * 255,
      a: alpha(srgb[4]),
    };
  }
  throw new Error(`fmContrast: cannot read the computed colour "${value}".`);
}

/** Source-over compositing of `top` on an opaque `bottom`. */
export function compositeOver(top: FmRgba, bottom: FmRgba): FmRgba {
  const a = top.a;
  return {
    r: top.r * a + bottom.r * (1 - a),
    g: top.g * a + bottom.g * (1 - a),
    b: top.b * a + bottom.b * (1 - a),
    a: 1,
  };
}

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function luminance({ r, g, b }: FmRgba): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

/** Contrast ratio of `fg` composited on the opaque `bg`. */
export function contrastRatio(fg: FmRgba, bg: FmRgba): number {
  const front = luminance(compositeOver(fg, bg));
  const back = luminance(bg);
  return (Math.max(front, back) + 0.05) / (Math.min(front, back) + 0.05);
}

const WHITE: FmRgba = { r: 255, g: 255, b: 255, a: 1 };

/** The opaque colour behind `element`: every ancestor background composited from the root down. */
export function effectiveBackground(element: Element): FmRgba {
  const chain: Element[] = [];
  for (let node: Element | null = element; node; node = node.parentElement) chain.push(node);
  return chain
    .reverse()
    .reduce(
      (behind, node) =>
        compositeOver(parseComputedColor(getComputedStyle(node).backgroundColor), behind),
      WHITE,
    );
}

/** A colour role resolved where `scope` renders: a probe inside it reads the computed value. */
export function resolveRoleColor(scope: Element, token: string): FmRgba {
  const probe = document.createElement('span');
  probe.setAttribute('aria-hidden', 'true');
  probe.style.color = `var(${token})`;
  scope.appendChild(probe);
  try {
    return parseComputedColor(getComputedStyle(probe).color);
  } finally {
    probe.remove();
  }
}

/** `4,8:1` in the owner's locale. */
export function formatContrast(ratio: number): string {
  return `${ratio.toLocaleString('fi-FI', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}:1`;
}
